<script setup lang="ts">
import { ref } from 'vue'
import { useAuthStore } from '@/stores'
import { revalidateCurrentRoute } from '@/router'

const { testid = 'account-switcher' } = defineProps<{ testid?: string }>()

const authStore = useAuthStore()
const isSwitching = ref(false)
const switchError = ref<string | null>(null)

function getRoleBadgeColor(role: string): string {
  return role === 'adult' ? 'primary' : 'secondary'
}

async function handleSwitch(accountId: string) {
  if (accountId === authStore.accountId) return
  isSwitching.value = true
  switchError.value = null
  try {
    await authStore.switchAccount(accountId)
    await revalidateCurrentRoute()
  } catch (e) {
    switchError.value = (e as Error).message
  } finally {
    isSwitching.value = false
  }
}
</script>

<template>
  <v-alert v-if="switchError" type="error" class="mb-4" closable @click:close="switchError = null">
    {{ switchError }}
  </v-alert>

  <v-list lines="two" :data-testid="testid">
    <v-list-item
      v-for="account in authStore.accounts"
      :key="account.account_id"
      :active="account.account_id === authStore.accountId"
      @click="handleSwitch(account.account_id)"
      :disabled="isSwitching"
    >
      <template #prepend>
        <v-icon v-if="account.account_id === authStore.accountId" color="primary">
          mdi-check-circle
        </v-icon>
        <v-icon v-else color="grey">mdi-circle-outline</v-icon>
      </template>
      <v-list-item-title>
        Family Account
      </v-list-item-title>
      <v-list-item-subtitle>
        <v-chip :color="getRoleBadgeColor(account.role)" size="small" variant="tonal">
          {{ account.role === 'adult' ? 'Adult' : 'Minor' }}
        </v-chip>
      </v-list-item-subtitle>
    </v-list-item>
  </v-list>

  <v-overlay :model-value="isSwitching" contained eager class="align-center justify-center" :data-testid="`${testid}-loading`">
    <v-progress-circular indeterminate />
  </v-overlay>
</template>
